import React, { forwardRef, type ButtonHTMLAttributes } from 'react';

/**
 * Segmented control — an exclusive "pick one of N peer views" selector, with
 * two visual treatments under one API:
 *
 *   - `variant="tray"` (default) — iOS-style: pill segments inside a
 *     `--bg-tertiary` tray, active segment raised on a `--bg-primary` chip +
 *     soft shadow (e.g. Cache Rules / Entries, Overview / Testing).
 *   - `variant="connected"` — a single bordered group with shared rounded
 *     corners (`overflow: hidden`); the active segment fills solid `--accent`
 *     with white text (e.g. the App Log source tabs).
 *   - `variant="pill"` — a wrapping `--bg-tertiary` band of rounded pills; the
 *     active pill fills solid `--accent` with white text (e.g. the Settings
 *     sub-tab strips, which wrap to multiple rows when a tab has many pages).
 *
 * Distinct from {@link Tabs} (underline indicator) and {@link ToggleButton}
 * (a single glow-outline toggle). `SegmentedControl` renders the group over an
 * `{ id, label }[]`; `Segment` is the single pill button, exported for
 * standalone composition.
 */

export type SegmentedVariant = 'tray' | 'connected' | 'pill';

export interface SegmentProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether this segment is the currently selected one. */
  active: boolean;
  variant?: SegmentedVariant;
}

function segmentStyle(variant: SegmentedVariant, active: boolean): React.CSSProperties {
  if (variant === 'connected') {
    return {
      padding: '8px 16px',
      border: 'none',
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      background: active ? 'var(--accent)' : 'var(--bg-tertiary)',
      color: active ? 'white' : 'var(--text-secondary)',
      transition: 'all 0.15s ease',
    };
  }
  if (variant === 'pill') {
    return {
      padding: '6px 14px',
      borderRadius: 7,
      border: 'none',
      fontSize: 13,
      fontWeight: active ? 600 : 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      background: active ? 'var(--accent)' : 'transparent',
      color: active ? '#fff' : 'var(--text-secondary)',
      transition: 'all 0.15s ease',
    };
  }
  return {
    padding: '6px 14px',
    borderRadius: 6,
    border: 'none',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    background: active ? 'var(--bg-primary)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
    transition: 'all 0.15s ease',
  };
}

export const Segment = forwardRef<HTMLButtonElement, SegmentProps>(function Segment(
  { active, variant = 'tray', children, style, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      style={{ ...segmentStyle(variant, active), ...style }}
      {...rest}
    >
      {children}
    </button>
  );
});

export interface SegmentItem<T extends string = string> {
  id: T;
  label: React.ReactNode;
  /** Optional tooltip shown on hover. */
  title?: string;
}

export interface SegmentedControlProps<T extends string = string> {
  items: ReadonlyArray<SegmentItem<T>>;
  active: T;
  onChange: (id: T) => void;
  variant?: SegmentedVariant;
  /** When set, the group is exposed as a labelled `role="group"` for screen readers. */
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}

function containerStyle(variant: SegmentedVariant): React.CSSProperties {
  if (variant === 'connected') {
    return {
      display: 'inline-flex',
      border: '1px solid var(--border)',
      borderRadius: 8,
      overflow: 'hidden',
    };
  }
  if (variant === 'pill') {
    return {
      display: 'inline-flex',
      flexWrap: 'wrap',
      gap: 4,
      padding: 4,
      background: 'var(--bg-tertiary)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      maxWidth: '100%',
    };
  }
  return {
    display: 'inline-flex',
    gap: 2,
    background: 'var(--bg-tertiary)',
    borderRadius: 8,
    padding: 3,
  };
}

export function SegmentedControl<T extends string = string>({
  items,
  active,
  onChange,
  variant = 'tray',
  ariaLabel,
  className,
  style,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={className}
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
      style={{ ...containerStyle(variant), ...style }}
    >
      {items.map((it) => (
        <Segment
          key={it.id}
          active={it.id === active}
          variant={variant}
          title={it.title}
          onClick={() => onChange(it.id)}
        >
          {it.label}
        </Segment>
      ))}
    </div>
  );
}
